import React, { useState } from "react";
import { toast } from "sonner";
import { Compass } from "./Ornaments";

// Chelsea Ray's Mailchimp list
const MC_BASE =
  "https://chelsearaywriting.us13.list-manage.com/subscribe/post-json";
const MC_U = "e0a3b1c7139d9b8333cfd043e";
const MC_ID = "e3e261c830";
const MC_F_ID = "0080c3e1f0";
// Honeypot input name (must be sent empty)
const MC_HONEYPOT = `b_${MC_U}_${MC_ID}`;

/**
 * Submit to Mailchimp via JSONP so the user stays on the page.
 * Resolves on Mailchimp response ({result, msg}) or rejects on network/timeout.
 */
function submitToMailchimp({ email, firstName }) {
  return new Promise((resolve, reject) => {
    const cbName = `_mc_cb_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
    const params = new URLSearchParams({
      u: MC_U,
      id: MC_ID,
      f_id: MC_F_ID,
      EMAIL: email,
      FNAME: firstName || "",
      [MC_HONEYPOT]: "",
      c: cbName,
    });

    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error("Request timed out. Please try again."));
    }, 12000);

    const script = document.createElement("script");
    script.src = `${MC_BASE}?${params.toString()}`;

    function cleanup() {
      clearTimeout(timeoutId);
      try {
        delete window[cbName];
      } catch {
        window[cbName] = undefined;
      }
      if (script.parentNode) script.parentNode.removeChild(script);
    }

    window[cbName] = (data) => {
      cleanup();
      resolve(data || {});
    };
    script.onerror = () => {
      cleanup();
      reject(new Error("Could not reach the sign-up service."));
    };
    document.body.appendChild(script);
  });
}

/** Strip HTML tags from Mailchimp's messages (e.g., "already subscribed" links). */
function stripHtml(str) {
  if (!str) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = String(str);
  return tmp.textContent || tmp.innerText || "";
}

export default function Newsletter() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please share your email to join the crew.");
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!emailOk) {
      toast.error("That email doesn't look quite right.");
      return;
    }

    setSubmitting(true);
    try {
      const data = await submitToMailchimp({
        email: email.trim(),
        firstName: firstName.trim(),
      });
      const msg = stripHtml(data.msg);
      if (data.result === "success") {
        setSubmitted(true);
        setFirstName("");
        setEmail("");
        toast.success("Welcome aboard. The crew has your coordinates.");
      } else if (
        msg.toLowerCase().includes("already subscribed") ||
        msg.toLowerCase().includes("is already")
      ) {
        // Treat "already subscribed" as a friendly success
        setSubmitted(true);
        setFirstName("");
        setEmail("");
        toast.success("You're already aboard, sailor.");
      } else {
        toast.error(msg || "Something went wrong. Please try again.");
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="relative py-24 md:py-32 bg-[#06090e] overflow-hidden reveal"
      aria-label="Join the newsletter"
    >
      <div className="mist opacity-50" aria-hidden="true" />
      <div className="stars opacity-50" aria-hidden="true" />

      <div className="relative max-w-3xl mx-auto px-6 md:px-10 text-center">
        <div
          className="mx-auto w-20 h-20 mb-6 opacity-90 reveal-scale"
          data-parallax-rotate="0.0015"
        >
          <Compass />
        </div>
        <p className="font-accent text-[10px] text-[#c9a25a] reveal-up">Newsletter</p>
        <h2
          data-testid="newsletter-title"
          className="font-display text-4xl md:text-5xl lg:text-6xl text-[#f2e8d5] mt-4 reveal-blur"
        >
          Join the Crew
        </h2>
        <p className="mt-6 text-[#a8b4c0] text-base md:text-lg leading-relaxed max-w-xl mx-auto reveal-up">
          Be the first to receive updates, bonus scenes, release news, and
          exclusive behind-the-scenes content.
        </p>

        {submitted ? (
          <div
            data-testid="newsletter-success"
            className="mt-12 border border-[#c9a25a]/40 bg-[#0a1118]/60 px-8 py-10 max-w-xl mx-auto"
          >
            <p className="font-display italic text-2xl text-[#e8d9b6]">
              Welcome aboard, sailor.
            </p>
            <p className="mt-3 text-[#a8b4c0]">
              Watch the horizon. A letter from the crew is on its way.
            </p>
          </div>
        ) : (
          <form
            data-testid="newsletter-form"
            onSubmit={onSubmit}
            className="mt-12 max-w-xl mx-auto text-left"
            noValidate
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label
                  htmlFor="firstName"
                  className="font-accent text-[10px] text-[#c9a25a]"
                >
                  First Name{" "}
                  <span className="lowercase tracking-normal text-[#a8b4c0]/60 italic">
                    (optional)
                  </span>
                </label>
                <input
                  id="firstName"
                  data-testid="newsletter-first-name"
                  type="text"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="parchment-input mt-2"
                  placeholder="Eleanor"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="font-accent text-[10px] text-[#c9a25a]"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  data-testid="newsletter-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="parchment-input mt-2"
                  placeholder="you@thesea.com"
                  required
                />
              </div>
            </div>

            {/* Anti-bot honeypot (must remain empty) */}
            <div
              aria-hidden="true"
              style={{ position: "absolute", left: "-5000px" }}
            >
              <label htmlFor={MC_HONEYPOT}>Leave this field empty</label>
              <input
                type="text"
                name={MC_HONEYPOT}
                id={MC_HONEYPOT}
                tabIndex={-1}
                defaultValue=""
                autoComplete="off"
              />
            </div>

            <div className="mt-10 text-center">
              <button
                data-testid="newsletter-submit"
                type="submit"
                disabled={submitting}
                className="btn-gold btn-gold-solid disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label="Join the Against All Waves newsletter"
              >
                {submitting ? "Sending…" : "Join the Newsletter"}
              </button>
              <p className="mt-5 text-xs text-[#a8b4c0]/70 italic">
                No spam. Only sea-bound stories.
              </p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
